import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, ArrowLeft, ShieldAlert, Image as ImageIcon, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AuthImage from "@/components/AuthImage";

function timeLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatBanDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

export default function Messages() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null); // friend object
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [imagePath, setImagePath] = useState(null); // staged image
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  // Refresh user once on page mount so chat_banned_until is up to date
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const banUntil = user && user.chat_banned_until ? new Date(user.chat_banned_until) : null;
  const isBanned = banUntil && banUntil.getTime() > Date.now();

  const loadConvos = useCallback(async () => {
    try {
      const r = await api.get("/dm/conversations");
      setConvos(r.data);
      // If query param ?to=<id>, open that convo
      const to = params.get("to");
      if (to && !active) {
        const c = r.data.find((x) => x.user.id === to);
        if (c) setActive(c.user);
      }
    } catch (e) {
      /* ignore */
    }
  }, [params, active]);

  useEffect(() => {
    loadConvos();
    const t = setInterval(loadConvos, 10000);
    return () => clearInterval(t);
  }, [loadConvos]);

  const loadMessages = useCallback(async () => {
    if (!active) return;
    try {
      const r = await api.get(`/dm/messages?with_id=${active.id}`);
      setMessages(r.data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [active]);

  useEffect(() => {
    if (!active) return undefined;
    loadMessages();
    const t = setInterval(loadMessages, 3000);
    return () => clearInterval(t);
  }, [active, loadMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!active) return;
    const hasText = text.trim().length > 0;
    if (!hasText && !imagePath) return;
    setErr("");
    try {
      await api.post("/dm/send", { to: active.id, text, image_path: imagePath || null });
      setText("");
      setImagePath(null);
      loadMessages();
      loadConvos();
    } catch (e2) {
      const msg = formatApiError(e2);
      setErr(msg);
      // If the API has just muted us, refresh the user object so the banner appears
      if (e2?.response?.status === 403) refresh();
    }
  };

  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setErr("Only image files are allowed.");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      setErr("Image too large (max 8MB).");
      return;
    }
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/upload", fd);
      setImagePath(r.data.path);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setUploading(false);
    }
  };

  const openConvo = (u) => {
    setActive(u);
    setMessages([]);
    setParams({ to: u.id });
  };

  return (
    <div
      className="fade-up flex flex-col h-[calc(100dvh-200px)] min-h-[400px]"
      data-testid="dm-page-root"
    >
      <div className="shrink-0">
        <p className="text-[#d4af37] text-xs uppercase tracking-[0.3em]">Direct · 私信</p>
        <h1 className="font-serif text-4xl mb-6">Conversations.</h1>

        {/* House rules */}
        <div className="mb-4 rounded-md border border-white/10 bg-[#141414] p-4 text-xs text-white/60 flex gap-3" data-testid="dm-house-rules">
          <ShieldAlert size={16} strokeWidth={1.5} className="text-[#d4af37] shrink-0 mt-0.5" />
          <div className="space-y-1 leading-relaxed">
            <p className="text-white/80">House rules · 私信守则</p>
            <p>
              • 仅管理员可分享联系方式（电话 / 微信 / 脸书 / IG / 小红书）；会员之间请保持平台内交流。
            </p>
            <p>• 禁止色情或不当内容。违规者将被禁言 7 天（Mute 7 days for any violation）。</p>
            <p>• 管理员有权审阅所有对话以维护社群秩序。</p>
          </div>
        </div>

        {isBanned && (
          <div
            className="mb-4 rounded-md border border-red-500/40 bg-red-950/20 p-4 text-sm text-red-200 flex gap-3"
            data-testid="dm-ban-banner"
          >
            <ShieldAlert size={18} strokeWidth={1.5} className="text-red-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-red-100">您因违反私信规定已被禁言</div>
              <div className="text-red-200/80 mt-0.5">
                You are muted until <span className="text-red-100">{formatBanDate(user.chat_banned_until)}</span>.
                如有疑问请联系管理员。
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4 flex-1 min-h-0 overflow-hidden">
        {/* Conversation list */}
        <div className={`border border-white/10 rounded-md overflow-y-auto min-h-0 ${active ? "hidden md:block" : ""}`}>
          {convos.length === 0 ? (
            <div className="p-6 text-white/40 text-sm" data-testid="dm-empty-list">
              Add friends to start messaging.
            </div>
          ) : (
            <div className="divide-y divide-white/5" data-testid="dm-convo-list">
              {convos.map((c) => (
                <button
                  key={c.user.id}
                  onClick={() => openConvo(c.user)}
                  className={`w-full text-left p-4 hover:bg-white/[0.03] transition ${
                    active?.id === c.user.id ? "bg-white/[0.05]" : ""
                  }`}
                  data-testid={`dm-convo-${c.user.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium truncate">{c.user.name}</div>
                    {c.unread > 0 && (
                      <span className="bg-[#d4af37] text-black text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                        {c.unread}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-white/40 truncate mt-1">
                    {c.last_message
                      ? (c.last_message.text || (c.last_message.image_path ? "📷 Image" : "No messages yet"))
                      : "No messages yet"}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Chat */}
        <div className={`border border-white/10 rounded-md flex flex-col min-h-0 ${active ? "" : "hidden md:flex"}`}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-white/40 text-sm" data-testid="dm-no-active">
              Select a conversation
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-white/10 flex items-center gap-3">
                <button
                  onClick={() => {
                    setActive(null);
                    setParams({});
                  }}
                  className="md:hidden text-white/60"
                  data-testid="dm-back"
                >
                  <ArrowLeft size={18} strokeWidth={1.5} />
                </button>
                <div>
                  <div className="font-medium">{active.name}</div>
                  <div className="text-xs text-white/40">{active.occupation}</div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2" ref={scrollRef} data-testid="dm-thread">
                {messages.length === 0 ? (
                  <p className="text-white/40 text-sm text-center mt-12">Say hello.</p>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_id === user.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${
                            mine
                              ? "bg-[#d4af37] text-black rounded-br-sm"
                              : "bg-[#1a1a1a] border border-white/10 text-white/90 rounded-bl-sm"
                          }`}
                          data-testid={`dm-msg-${m.id}`}
                        >
                          {m.image_path && (
                            <AuthImage
                              path={m.image_path}
                              className="rounded-lg mb-1.5 max-h-72 object-contain bg-black/20"
                              data-testid={`dm-msg-image-${m.id}`}
                            />
                          )}
                          {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                          <div className={`text-[10px] mt-1 ${mine ? "text-black/50" : "text-white/40"}`}>
                            {timeLabel(m.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {err && <p className="px-4 text-red-400 text-xs" data-testid="dm-error">{err}</p>}
              {imagePath && (
                <div className="px-3 pt-2 flex items-center gap-2" data-testid="dm-staged-image">
                  <div className="relative">
                    <AuthImage path={imagePath} className="h-16 w-16 object-cover rounded-md border border-white/15" />
                    <button
                      type="button"
                      onClick={() => setImagePath(null)}
                      className="absolute -top-1.5 -right-1.5 bg-black/80 border border-white/20 rounded-full p-0.5 text-white/80 hover:text-white"
                      data-testid="dm-staged-image-remove"
                      aria-label="Remove image"
                    >
                      <X size={12} strokeWidth={2} />
                    </button>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-white/40">Image ready</span>
                </div>
              )}
              <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2 items-center shrink-0 bg-[#0a0a0a]">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onPickImage}
                  data-testid="dm-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={isBanned || uploading}
                  className="text-white/60 hover:text-[#d4af37] p-2 disabled:opacity-30"
                  data-testid="dm-attach-image"
                  aria-label="Attach image"
                  title="Attach image"
                >
                  <ImageIcon size={18} strokeWidth={1.5} />
                </button>
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onFocus={(e) => {
                    // iOS Safari: scroll the input back into view above the on-screen keyboard.
                    setTimeout(() => {
                      try { e.target.scrollIntoView({ block: "center", behavior: "smooth" }); } catch { /* ignore */ }
                    }, 250);
                  }}
                  placeholder={isBanned ? "您当前已被禁言…" : uploading ? "Uploading image…" : "Type a message…"}
                  disabled={isBanned}
                  enterKeyHint="send"
                  className="flex-1 bg-transparent border-b border-white/10 focus:border-[#d4af37] outline-none py-2 px-1 disabled:opacity-40"
                  data-testid="dm-input"
                />
                <button
                  type="submit"
                  disabled={isBanned || uploading || (!text.trim() && !imagePath)}
                  className="bg-[#d4af37] text-black px-4 py-2 disabled:opacity-30 flex items-center gap-2 text-xs uppercase tracking-widest"
                  data-testid="dm-send"
                >
                  <Send size={14} strokeWidth={2} />
                  Send
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
